import { useState } from "react";
import { useV1InventoryMaterialsList, v1InventoryMaterialsList } from "@/api/generated/v1/v1";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";
import { PageBanner } from "@/shared/components/PageBanner";

export function MaterialListPage() {
  const navigate = useNavigate();
  const canCreate = usePermission("inventory.manage");
  
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Hook params include page and filters
  const queryParams = { 
    page: page,
    page_size: pageSize,
    ...(searchTerm && { search: searchTerm }),
    ...activeFilters 
  };
  
  const { data, isLoading, isError, error } = useV1InventoryMaterialsList(queryParams);

  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Reset page on filter change
  };

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1InventoryMaterialsList({
        export: "true",
        ...(searchTerm && { search: searchTerm }),
        ...activeFilters
      } as any);
      // Handle truncated warning if needed
      if ((res as any).truncated) {
        alert("تعداد رکوردها بیش از ۱۰,۰۰۰ مورد است و اکسپورت محدود شد.");
      }
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const TYPE_LABELS: Record<string, string> = {
    raw_material: "مواد اولیه",
    semi_finished: "نیمه‌ساخته",
    finished_product: "محصول نهایی",
    spare_part: "قطعه یدکی",
  };

  const columns = [
    { header: "کد خانواده", accessor: (row: any) => row.code },
    { header: "نوع", accessor: (row: any) => row.material_type ? TYPE_LABELS[row.material_type] || row.material_type : "—" },
    { header: "واحد اندازه‌گیری", accessor: (row: any) => row.uom_code },
    { header: "نام (آخرین ریویژن)", accessor: (row: any) => row.latest_published_revision?.name ?? "—" },
    { header: "وضعیت", accessor: (row: any) => row.latest_published_revision?.is_active ? "فعال" : "غیرفعال" },
  ];

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "materials_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "materials_export");
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="مدیریت متریال‌ها (Materials)"
        subtitle="مدیریت خانواده متریال‌ها، کاتالوگ قطعات و ریویژن‌ها"
        icon="📦"
        helpLink="/help/inventory/material-list"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
          {canCreate && (
            <>
              <button
                onClick={() => navigate("/inventory/materials/import")}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                }}
              >
                📥 ایمپورت Excel
              </button>
              <button
                onClick={() => navigate("/inventory/materials/create")}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>➕</span>
                <span>ایجاد قطعه جدید</span>
              </button>
            </>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem", maxWidth: "100%", overflowX: "auto" }}>

        <DataTableToolbar
          onSearch={(term) => {
            setSearchTerm(term);
            setPage(1); // Reset page on search
          }}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          dateRangeFilters={[{ field: "published_at", label: "تاریخ انتشار" }]}
          filters={[
            {
              name: "material_type",
              label: "نوع متریال",
              options: [
                { label: "مواد اولیه (Raw Material)", value: "raw_material" },
                { label: "نیمه‌ساخته (Semi-Finished)", value: "semi_finished" },
                { label: "محصول نهایی (Finished Product)", value: "finished_product" },
                { label: "قطعه یدکی (Spare Part)", value: "spare_part" },
              ],
            },
            {
              name: "is_active",
              label: "وضعیت",
              options: [
                { label: "فعال", value: "True" },
                { label: "غیرفعال", value: "False" },
              ],
            },
          ]}
        />

        {isLoading && (
          <div className="flex-center p-8">
            <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری اطلاعات...</p>
          </div>
        )}

        {isError && (
          <div className="p-4 mb-4" style={{ background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red", borderRadius: "8px" }}>
            <p style={{ color: "red" }}>خطا در دریافت اطلاعات: {(error as Error)?.message}</p>
          </div>
        )}

        {!isLoading && !isError && data?.results && (
          <div className="table-container" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>کد خانواده</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>نوع</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>واحد اندازه‌گیری</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>نام (آخرین ریویژن)</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>وضعیت</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {data.results.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      هیچ متریالی یافت نشد.
                    </td>
                  </tr>
                ) : (
                  data.results.map((item) => (
                    <tr key={item.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "1rem" }} dir="ltr" className="text-left font-mono">{item.code}</td>
                      <td style={{ padding: "1rem" }}>{item.material_type ? TYPE_LABELS[item.material_type] || item.material_type : "—"}</td>
                      <td style={{ padding: "1rem", fontWeight: "bold" }}>{item.uom_code}</td>
                      <td style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                        {item.latest_published_revision?.name ?? "—"}
                      </td>
                      <td style={{ padding: "1rem" }}>
                        <span style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "100px",
                          background: item.latest_published_revision?.is_active
                            ? "rgba(0,255,0,0.1)" : "rgba(128,128,128,0.15)",
                          color: item.latest_published_revision?.is_active
                            ? "var(--color-success)" : "var(--color-text-muted)",
                        }}>
                          {item.latest_published_revision
                            ? (item.latest_published_revision.is_active ? "فعال" : "غیرفعال")
                            : "بدون ریویژن"}
                        </span>
                      </td>
                      <td style={{ padding: "1rem" }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "0.85rem", padding: "0.3rem 0.75rem" }}
                          onClick={() => navigate(`/inventory/materials/${item.id}`)}
                        >
                          مشاهده
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {data?.count !== undefined && (
          <div className="mt-4 pt-4 border-t flex-between" style={{ borderColor: "var(--glass-border)", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <span>تعداد کل: {data.count}</span>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <span>نمایش در صفحه:</span>
                <select 
                  className="input" 
                  style={{ padding: "0.2rem 1.5rem 0.2rem 0.5rem", width: "auto" }}
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>۱۰</option>
                  <option value={25}>۲۵</option>
                  <option value={50}>۵۰</option>
                  <option value={100}>۱۰۰</option>
                  <option value={1000}>همه</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <button 
                type="button"
                className="btn btn-secondary" 
                style={{ padding: "0.2rem 0.6rem" }}
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                قبلی
              </button>
              <span>صفحه {page} از {Math.ceil(data.count / pageSize) || 1}</span>
              <button 
                type="button"
                className="btn btn-secondary" 
                style={{ padding: "0.2rem 0.6rem" }}
                disabled={page >= Math.ceil(data.count / pageSize)}
                onClick={() => setPage(p => p + 1)}
              >
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}